/**
 * VR emote panel: the desktop emote wheel's motions plus the Tripo library
 * categories, as a 3D panel. Opened from the VR user menu; replaces the menu
 * while open. In VR your own body follows your head and hands, so a motion is
 * what OTHER players see on your avatar (Selfie shows it too).
 */
import React, { useState } from 'react';
import { Text } from '@react-three/drei';
import { vrColors as colors, noRaycast, Surface } from './vrUi';
import { useWorldStore } from '../../stores/worldStore';
import { broadcastEmote } from '../../services/worldBroadcast';
import { getCurrentWorldId } from '../../services/realtimeManager';
import { EMOTES, EMOTE_MAP, LIBRARY_EMOTES, LIBRARY_CATEGORIES, SIT_EMOTES } from '../../config/emotes';
import { gesturesEnabled, setGesturesEnabled } from './hands/handGestures';

const COLS = 4, ROWS = 4, PER_PAGE = COLS * ROWS;
const W = .76;

function Button({ label, x, y, w, h = .05, active = false, disabled = false, fontSize = .016, onPress }: {
  label: string; x: number; y: number; w: number; h?: number; active?: boolean; disabled?: boolean; fontSize?: number; onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const bg = disabled ? '#1a2633' : active ? colors.teal : hover ? colors.hover : colors.card;
  return <group position={[x, y, .006]} name={`vr-emote-${label}`}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onClick={(e) => { e.stopPropagation(); if (!disabled) onPress(); }}>
    <Surface width={w} height={h} color={bg} r={.01} />
    <Text raycast={noRaycast} position={[0, 0, .003]} fontSize={fontSize} maxWidth={w - .012} textAlign="center"
      color={disabled ? colors.muted : active ? '#04211e' : colors.text}>{label}</Text>
  </group>;
}

const title = (t: string) => (t === 'mine' ? 'Favorites' : `${t[0].toUpperCase()}${t.slice(1)}`);

export default function VREmotePanel({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState('mine');
  const [page, setPage] = useState(0);
  const [gestures, setGestures] = useState(gesturesEnabled);
  const active = useWorldStore((s) => s.localEmote?.name);
  const tabs = ['mine', ...LIBRARY_CATEGORIES.filter((c) => LIBRARY_EMOTES.some((e) => e.category === c))];
  // Sits need a seat (menu "Sit" finds one), so they're left out here.
  const list = tab === 'mine' ? EMOTES.filter((e) => !SIT_EMOTES.has(e.name)) : LIBRARY_EMOTES.filter((e) => e.category === tab);
  const pages = Math.max(1, Math.ceil(list.length / PER_PAGE));
  const shown = list.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  // actualWorldId is only set in the editor; the viewer's channel knows its world.
  const worldId = () => useWorldStore.getState().actualWorldId || getCurrentWorldId();
  const play = (name: string) => {
    useWorldStore.getState().playLocalEmote(name);
    const id = worldId();
    if (id) broadcastEmote(id, name);
  };
  const stop = () => {
    useWorldStore.getState().stopLocalEmote();
    const id = worldId();
    if (id) broadcastEmote(id, null);
  };

  // Layout (metres, panel-local): header, two tab rows, 4×4 grid, footer.
  const H = .56;
  const top = H / 2;
  const tabW = (W - .04 - .01 * 3) / 4;
  const cellW = (W - .04 - .01 * (COLS - 1)) / COLS, cellH = .058;
  const gridTop = top - .19;
  return <group onClick={(e) => e.stopPropagation()}>
    <Surface width={W + .008} height={H + .008} color={colors.border} z={-.003} />
    <Surface width={W} height={H} color={colors.panel} />
    <Text raycast={noRaycast} position={[-W / 2 + .02, top - .035, .006]} anchorX="left" fontSize={.021} color={colors.text}>
      {`Emotes · ${title(tab)}`}
    </Text>
    <Button label={gestures ? 'Gestures: on' : 'Gestures: off'} x={W / 2 - .315} y={top - .035} w={.17} h={.042} fontSize={.0135} active={gestures}
      onPress={() => { setGesturesEnabled(!gestures); setGestures(!gestures); }} />
    <Button label="Stop" x={W / 2 - .17} y={top - .035} w={.1} h={.042} disabled={!active} onPress={stop} />
    <Button label="Close" x={W / 2 - .06} y={top - .035} w={.1} h={.042} onPress={onClose} />

    {tabs.map((t, i) => (
      <Button key={t} label={title(t)} w={tabW} h={.042} fontSize={.0145} active={tab === t}
        x={-W / 2 + .02 + tabW / 2 + (i % 4) * (tabW + .01)} y={top - .085 - Math.floor(i / 4) * .05}
        onPress={() => { setTab(t); setPage(0); }} />
    ))}

    {shown.map((e, i) => (
      <Button key={e.name} label={e.label} w={cellW} h={cellH} fontSize={.0155} active={active === e.name}
        x={-W / 2 + .02 + cellW / 2 + (i % COLS) * (cellW + .01)} y={gridTop - Math.floor(i / COLS) * (cellH + .01)}
        onPress={() => play(e.name)} />
    ))}

    {pages > 1 && <>
      <Button label="‹ Prev" x={-.12} y={-top + .035} w={.12} h={.042} disabled={page === 0} onPress={() => setPage((p) => p - 1)} />
      <Text raycast={noRaycast} position={[0, -top + .035, .006]} fontSize={.015} color={colors.muted}>{`${page + 1} / ${pages}`}</Text>
      <Button label="Next ›" x={.12} y={-top + .035} w={.12} h={.042} disabled={page >= pages - 1} onPress={() => setPage((p) => p + 1)} />
    </>}
    {pages === 1 && <Text raycast={noRaycast} position={[0, -top + .035, .006]} fontSize={.0135} color={colors.muted}>
      {active && EMOTE_MAP[active]?.loop ? 'Looping — press Stop to end it' : gestures ? 'Or use your hands: wave, clap, thumbs up, heart, hands up' : 'Others see your avatar do it'}
    </Text>}
  </group>;
}
